import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Columns4, CheckCircle } from 'lucide-react';
import { SubscriptionPlan } from '@/types/subscriptionPlans';
import PlanCard from './PlanCard';
import { PlanType } from '@/hooks/useAdminPlans';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface PlanSelectionSectionProps {
  plans: SubscriptionPlan[];
  selectedPlanId: string | null;
  selectedPlanType: PlanType;
  isProcessing: boolean;
  isTrialUser: boolean;
  onSelectPlan: (planId: string, planType: PlanType) => void;
  onPurchasePlan: () => void;
}

const PlanSelectionSection: React.FC<PlanSelectionSectionProps> = ({
  plans,
  selectedPlanId,
  selectedPlanType,
  isProcessing,
  isTrialUser,
  onSelectPlan,
  onPurchasePlan
}) => {
  const [billingPeriod, setBillingPeriod] = useState<PlanType>(selectedPlanType || 'monthly');
  
  useEffect(() => {
    if (selectedPlanType) {
      setBillingPeriod(selectedPlanType);
    }
  }, [selectedPlanType]);
  
  const handleBillingPeriodChange = (value: string) => {
    const newPeriod = value as PlanType;
    setBillingPeriod(newPeriod);
    if (selectedPlanId) {
      onSelectPlan(selectedPlanId, newPeriod);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-center mb-4">
        <Tabs 
          value={billingPeriod} 
          onValueChange={handleBillingPeriodChange}
          className="w-full max-w-md"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
            <TabsTrigger value="quarterly">
              Quarterly <span className="text-xs text-green-600 ml-1">Save 15%</span>
            </TabsTrigger>
            <TabsTrigger value="yearly">
              Yearly <span className="text-xs text-green-600 ml-1">Save 20%</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      <Card className="border-none shadow-lg bg-gradient-to-br from-white to-blue-50">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-primary-blue flex items-center justify-center gap-2">
            <Columns4 className="h-6 w-6" />
            {isTrialUser ? 'Upgrade Your Subscription' : 'Choose Your Plan'}
          </CardTitle>
          <CardDescription className="text-center text-gray-600">
            All plans include a 7-day trial.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                selectedPlanId={selectedPlanId}
                selectedPlanType={billingPeriod}
                onSelectPlan={onSelectPlan}
              />
            ))}
          </div>
          
          {selectedPlanId && (
            <div className="mt-8 text-center">
              <Button
                size="lg"
                onClick={onPurchasePlan}
                disabled={isProcessing}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-2 rounded-full shadow-md transform transition-all hover:scale-105"
              >
                {isProcessing ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    {isTrialUser ? 'Continue to Checkout' : 'Purchase Plan'}
                  </span>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlanSelectionSection;
